import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, landlordAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return null;
    }

    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      // Auto-detect timezone for landlords
      if (response.data?.role === 'landlord') {
        syncTimezone();
      }
      return response.data;
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  // Auto-detect and sync timezone for landlords
  const syncTimezone = useCallback(async () => {
    try {
      const settingsRes = await landlordAPI.getSettings();
      const savedTz = settingsRes.data?.timezone;
      if (savedTz) {
        localStorage.setItem('u_dorm_timezone', savedTz);
      } else {
        // First time: auto-detect from browser and save
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        localStorage.setItem('u_dorm_timezone', browserTz);
        await landlordAPI.updateSettings({ timezone: browserTz });
      }
    } catch (e) {
      // Fallback: use browser timezone
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      localStorage.setItem('u_dorm_timezone', browserTz);
    }
  }, []);

  // Expose refreshUser to allow components to refresh user data
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    if (userData?.role === 'landlord') {
      syncTimezone();
    }
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const googleLogin = async (sessionId) => {
    const response = await authAPI.googleSession(sessionId);
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updatedData) => {
    const newUser = { ...user, ...updatedData };
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const value = {
    user,
    loading,
    login,
    register,
    googleLogin,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
